import { useState, useEffect } from 'react';
import { X, Loader2, Search, ChevronRight, Plus, Check } from 'lucide-react';
import api from '../../lib/api';
import { useFundraiserStore } from '../../store/fundraiserStore';
import toast from 'react-hot-toast';
import type { CompetitorFundraiser } from '../../types';
import { formatSportLabel } from '../../lib/sportLabel';

interface LinkFundraiserModalProps {
  isOpen: boolean;
  fundraiser: CompetitorFundraiser | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface OrgResult {
  id: string;
  name: string;
  state: string | null;
}

interface SportResult {
  id: string;
  name: string;
  gender: string;
}

const COMMON_SPORTS = [
  'Football', 'Basketball', 'Baseball', 'Softball', 'Soccer', 'Volleyball',
  'Track & Field', 'Tennis', 'Golf', 'Swimming', 'Wrestling', 'Lacrosse',
  'Hockey', 'Cheer', 'Dance', 'Band',
];

const GENDER_OPTIONS = [
  { value: 'BOYS', label: 'Boys' },
  { value: 'GIRLS', label: 'Girls' },
  { value: 'COED', label: 'Co-Ed' },
];

const SEASON_MAP: Record<string, string> = {
  'Football': 'FALL', 'Soccer': 'FALL', 'Volleyball': 'FALL', 'Cheer': 'FALL',
  'Basketball': 'WINTER', 'Wrestling': 'WINTER', 'Swimming': 'WINTER', 'Hockey': 'WINTER', 'Dance': 'WINTER',
  'Baseball': 'SPRING', 'Softball': 'SPRING', 'Tennis': 'SPRING', 'Golf': 'SPRING',
  'Track & Field': 'SPRING', 'Lacrosse': 'SPRING', 'Band': 'FALL',
};

function parseSportText(text: string | null | undefined): { name: string; gender: string } | null {
  if (!text) return null;
  const t = text.trim();
  if (!t) return null;

  let gender = 'BOYS';
  let name = t;

  if (/^boys?\s+/i.test(t)) { gender = 'BOYS'; name = t.replace(/^boys?\s+/i, ''); }
  else if (/^girls?\s+/i.test(t)) { gender = 'GIRLS'; name = t.replace(/^girls?\s+/i, ''); }
  else if (/^co-?ed\s+/i.test(t)) { gender = 'COED'; name = t.replace(/^co-?ed\s+/i, ''); }

  name = name.replace(/\b\w/g, c => c.toUpperCase()).trim();
  return name ? { name, gender } : null;
}

const currentYear = new Date().getFullYear();

export const LinkFundraiserModal = ({
  isOpen,
  fundraiser,
  onClose,
  onSuccess,
}: LinkFundraiserModalProps) => {
  const { updateFundraiser } = useFundraiserStore();

  // Org search
  const [searchQuery, setSearchQuery] = useState('');
  const [orgResults, setOrgResults] = useState<OrgResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<OrgResult | null>(null);

  // Sport selection
  const [sports, setSports] = useState<SportResult[]>([]);
  const [isLoadingSports, setIsLoadingSports] = useState(false);
  const [selectedSportId, setSelectedSportId] = useState<string | null>(null);

  // Create sport inline
  const [showCreateSport, setShowCreateSport] = useState(false);
  const [newSportName, setNewSportName] = useState('');
  const [newSportGender, setNewSportGender] = useState('BOYS');
  const [newSportSeason, setNewSportSeason] = useState('FALL');
  const [isCreatingSport, setIsCreatingSport] = useState(false);

  // Fundraiser data fields
  const [raisedAmount, setRaisedAmount] = useState('');
  const [goalAmount, setGoalAmount] = useState('');
  const [year, setYear] = useState('');
  const [status, setStatus] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on open
  useEffect(() => {
    if (isOpen && fundraiser) {
      setSearchQuery(fundraiser.schoolName || '');
      setSelectedOrg(null);
      setSports([]);
      setSelectedSportId(null);
      setShowCreateSport(false);
      setOrgResults([]);
      setRaisedAmount(fundraiser.raisedAmount != null ? String(fundraiser.raisedAmount) : '');
      setGoalAmount(fundraiser.goalAmount != null ? String(fundraiser.goalAmount) : '');
      setYear(fundraiser.year != null ? String(fundraiser.year) : '');
      setStatus(fundraiser.status || '');
      setError(null);
    }
  }, [isOpen, fundraiser]);

  // Debounced typeahead
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setOrgResults([]);
      return;
    }
    const timer = setTimeout(() => searchOrgs(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchOrgs = async (q: string) => {
    setIsSearching(true);
    try {
      const { data } = await api.get(`/api/organizations/search?q=${encodeURIComponent(q)}&limit=30`);
      setOrgResults(data.organizations || []);
    } catch {
      setOrgResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectOrg = async (org: OrgResult) => {
    setSelectedOrg(org);
    setSelectedSportId(null);
    setShowCreateSport(false);
    setIsLoadingSports(true);
    try {
      const { data } = await api.get(`/api/organizations/${org.id}`);
      const orgSports = (data.organization?.sports || []).filter((s: any) => s.isActive);
      setSports(orgSports.map((s: any) => ({ id: s.id, name: s.name, gender: s.gender })));
    } catch {
      setSports([]);
    } finally {
      setIsLoadingSports(false);
    }
  };

  const handleCreateSport = async () => {
    if (!selectedOrg || !newSportName.trim()) return;
    setIsCreatingSport(true);
    try {
      const { data } = await api.post(`/api/organizations/${selectedOrg.id}/sports`, {
        name: newSportName.trim(),
        gender: newSportGender,
        season: newSportSeason,
      });
      const newSport = { id: data.sport.id, name: data.sport.name, gender: data.sport.gender };
      setSports(prev => [...prev, newSport]);
      setSelectedSportId(newSport.id);
      setShowCreateSport(false);
      toast.success(`Created ${formatSportLabel(newSportGender, newSportName.trim())}`);
    } catch (err: any) {
      if (err.response?.status === 409) {
        toast.error('Sport already exists — select it from the list');
      } else {
        toast.error('Failed to create sport');
      }
    } finally {
      setIsCreatingSport(false);
    }
  };

  const openCreateSport = () => {
    const parsed = parseSportText(fundraiser?.sport);
    if (parsed) {
      setNewSportName(parsed.name);
      setNewSportGender(parsed.gender);
      setNewSportSeason(SEASON_MAP[parsed.name] || 'FALL');
    } else {
      setNewSportName('');
      setNewSportGender('BOYS');
      setNewSportSeason('FALL');
    }
    setShowCreateSport(true);
  };

  const handleSave = async () => {
    if (!fundraiser || !selectedOrg) return;
    setIsSaving(true);
    setError(null);
    try {
      const updateData: Record<string, any> = {
        organizationId: selectedOrg.id,
      };
      if (selectedSportId) updateData.sportId = selectedSportId;
      if (raisedAmount) updateData.raisedAmount = parseFloat(raisedAmount);
      if (goalAmount) updateData.goalAmount = parseFloat(goalAmount);
      if (year) updateData.year = parseInt(year, 10);
      if (status) updateData.status = status;

      await updateFundraiser(fundraiser.id, updateData);
      toast.success(`Linked to ${selectedOrg.name}`);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !fundraiser) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Link Fundraiser
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {fundraiser.schoolName} — {fundraiser.platform.replace('_', ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 space-y-5">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            {/* Organization search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Organization <span className="text-red-500">*</span>
              </label>
              {selectedOrg ? (
                <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedOrg.name}</span>
                    {selectedOrg.state && <span className="text-xs text-gray-500">{selectedOrg.state}</span>}
                  </div>
                  <button
                    onClick={() => { setSelectedOrg(null); setSports([]); setSelectedSportId(null); setShowCreateSport(false); }}
                    className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="input pl-9"
                      placeholder="Type to search organizations..."
                      autoFocus
                    />
                    {isSearching && (
                      <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                    )}
                  </div>
                  {orgResults.length > 0 && (
                    <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                      {orgResults.map((org) => (
                        <button
                          key={org.id}
                          onClick={() => handleSelectOrg(org)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-sm flex items-center justify-between border-b border-gray-100 dark:border-gray-700 last:border-0"
                        >
                          <span className="text-gray-900 dark:text-gray-100">{org.name}</span>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {org.state && <span className="text-xs text-gray-400">{org.state}</span>}
                            <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {orgResults.length === 0 && !isSearching && searchQuery.trim().length >= 2 && (
                    <p className="text-xs text-gray-400 text-center py-1">No organizations found</p>
                  )}
                </div>
              )}
            </div>

            {/* Sport selection — only after org is selected */}
            {selectedOrg && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sport
                </label>
                {isLoadingSports ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading sports...
                  </div>
                ) : showCreateSport ? (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">New Sport</p>
                    <input
                      type="text"
                      value={newSportName}
                      onChange={(e) => {
                        setNewSportName(e.target.value);
                        const season = SEASON_MAP[e.target.value.trim()];
                        if (season) setNewSportSeason(season);
                      }}
                      list="common-sports-modal"
                      className="input text-sm"
                      placeholder="e.g., Football"
                      autoFocus
                    />
                    <datalist id="common-sports-modal">
                      {COMMON_SPORTS.map(s => <option key={s} value={s} />)}
                    </datalist>
                    <div className="flex gap-2">
                      <select value={newSportGender} onChange={(e) => setNewSportGender(e.target.value)} className="input text-sm flex-1">
                        {GENDER_OPTIONS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                      </select>
                      <select value={newSportSeason} onChange={(e) => setNewSportSeason(e.target.value)} className="input text-sm flex-1">
                        <option value="FALL">Fall</option>
                        <option value="WINTER">Winter</option>
                        <option value="SPRING">Spring</option>
                        <option value="SUMMER">Summer</option>
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleCreateSport}
                        disabled={isCreatingSport || !newSportName.trim()}
                        className="btn-primary text-sm py-1.5 flex-1"
                      >
                        {isCreatingSport ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Create Sport'}
                      </button>
                      <button onClick={() => setShowCreateSport(false)} className="btn-secondary text-sm py-1.5">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {sports.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setSelectedSportId(selectedSportId === s.id ? null : s.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-colors ${
                          selectedSportId === s.id
                            ? 'bg-blast-50 dark:bg-blast-900/20 border border-blast-200 dark:border-blast-700 text-blast-700 dark:text-blast-300'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-900 dark:text-gray-100 border border-transparent'
                        }`}
                      >
                        {formatSportLabel(s.gender, s.name)}
                        {selectedSportId === s.id && <Check className="h-4 w-4" />}
                      </button>
                    ))}
                    {sports.length === 0 && (
                      <p className="text-xs text-gray-400 py-1">No sports at this organization yet.</p>
                    )}
                    <button
                      onClick={openCreateSport}
                      className="w-full text-left px-3 py-2 rounded-lg text-sm text-blast-600 dark:text-blast-400 hover:bg-blast-50 dark:hover:bg-blast-900/10 flex items-center gap-1.5 border border-dashed border-gray-300 dark:border-gray-600"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Create new sport
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Fundraiser data fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fundraiser Data
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Amount Raised</label>
                  <input
                    type="number"
                    value={raisedAmount}
                    onChange={(e) => setRaisedAmount(e.target.value)}
                    className="input text-sm"
                    placeholder="$0"
                    min={0}
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Goal Amount</label>
                  <input
                    type="number"
                    value={goalAmount}
                    onChange={(e) => setGoalAmount(e.target.value)}
                    className="input text-sm"
                    placeholder="$0"
                    min={0}
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Year</label>
                  <input
                    type="number"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    className="input text-sm"
                    min={currentYear - 5}
                    max={currentYear + 2}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="input text-sm"
                  >
                    <option value="">—</option>
                    <option value="ACTIVE">Active</option>
                    <option value="ENDED">Ended</option>
                    <option value="UNKNOWN">Unknown</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end space-x-3">
            <button onClick={onClose} className="btn-secondary" disabled={isSaving}>
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="btn-primary"
              disabled={isSaving || !selectedOrg}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Link & Save'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
